import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';

import { startWith } from 'rxjs/operators';

import { NavItem, RoleNavService } from '@core/auth/role-nav.service';
import { SportsService } from '@core/sports/sports.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';

import { IconComponent, IconName } from '@shared/components/icon/icon.component';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: IconName;
  group: string;
  run: () => void;
}

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, IconComponent],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-command-palette',
    '(keydown)': 'onKey($event)'
  }
})
export class CommandPaletteComponent {
  private readonly dialogRef = inject(MatDialogRef<CommandPaletteComponent>);
  private readonly router = inject(Router);
  private readonly nav = inject(RoleNavService);
  private readonly tenants = inject(TenantContextService);
  private readonly sports = inject(SportsService);

  protected readonly searchInput = viewChild<{ nativeElement: HTMLInputElement }>('searchEl');
  protected readonly query = new FormControl('', { nonNullable: true });
  private readonly q = toSignal(this.query.valueChanges.pipe(startWith('')), { initialValue: '' });

  protected readonly highlightIndex = signal(0);

  private readonly all = computed<CommandItem[]>(() => {
    const items: CommandItem[] = [];
    for (const sec of this.nav.sections()) {
      for (const it of sec.items) {
        items.push(this.toNavCommand(it, sec.title || 'Navigate'));
      }
    }
    if (this.tenants.isRoot()) {
      for (const t of this.tenants.orgTenants()) {
        items.push({
          id: `tenant:${t.slug}`,
          label: t.name,
          hint: t.slug,
          icon: 'building',
          group: 'Switch tenant',
          run: () => this.tenants.setCurrentTenantBySlug(t.slug)
        });
      }
    }
    for (const s of this.sports.availableSports()) {
      items.push({
        id: `sport:${s.key}`,
        label: s.label,
        hint: s.key,
        icon: 'star',
        group: 'Switch sport',
        run: () => this.sports.setCurrentSport(s.key)
      });
    }
    return items;
  });

  protected readonly filtered = computed(() => {
    const term = this.q().trim().toLowerCase();
    const items = this.all();
    if (!term) return items;
    return items.filter(c => fuzzy(`${c.label} ${c.hint ?? ''} ${c.group}`.toLowerCase(), term));
  });

  protected readonly groups = computed(() => {
    const map = new Map<string, CommandItem[]>();
    for (const c of this.filtered()) {
      const list = map.get(c.group) ?? [];
      list.push(c);
      map.set(c.group, list);
    }
    return [...map.entries()].map(([group, items]) => ({ group, items }));
  });

  private readonly flat = computed(() => this.filtered());

  protected onKey(event: KeyboardEvent): void {
    const len = this.flat().length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightIndex.update(i => Math.min(i + 1, Math.max(0, len - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightIndex.update(i => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = this.flat()[this.highlightIndex()];
      if (item) this.select(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.dialogRef.close();
    }
  }

  protected select(item: CommandItem): void {
    item.run();
    this.dialogRef.close();
  }

  protected indexOf(item: CommandItem): number {
    return this.flat().findIndex(c => c.id === item.id);
  }

  private toNavCommand(it: NavItem, group: string): CommandItem {
    return {
      id: `nav:${it.route}`,
      label: it.label,
      hint: it.route,
      icon: it.icon,
      group: group || 'Navigate',
      run: () => {
        void this.router.navigateByUrl(it.route);
      }
    };
  }
}

function fuzzy(haystack: string, needle: string): boolean {
  if (!needle) return true;
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return false;
}
